import { Disclosure } from '@headlessui/react';
import { ChevronDownIcon, PhoneIcon } from '@heroicons/react/24/outline';
import React, { useMemo } from 'react';

import { TreatmentRecordRequestSchema } from '@/services/api/generated/treatmentRecordRequestsService';
import { MedicalProvider } from '@/services/types/treatment-types';
import { getExtendedMetadata } from '@/types/treatment-record-request-extended';
import {
  getIntervalLabel,
  isVoiceAIApplicable,
  willVoiceAIBeApplicable,
} from '@/utils/voiceAiScheduling';

import { DEFAULT_VOICE_AI_INTERVAL, StatusOptions } from '../../constants';
import { useVoiceAISchedule } from '../hooks/useVoiceAISchedule';
import {
  RecordsPizzaTracker,
  REQUEST_STEPS,
} from '../status-tracker/RequestStatusTracker';

interface RequestCardOverviewProps {
  request: TreatmentRecordRequestSchema;
  provider: MedicalProvider;
  matterId: string;
}

export const RequestCardOverview: React.FC<RequestCardOverviewProps> = ({
  request,
  provider,
  matterId,
}) => {
  const extendedMetadata = getExtendedMetadata(request.metadata);
  const voiceInterval =
    extendedMetadata.voice_ai_check_interval_days || DEFAULT_VOICE_AI_INTERVAL;
  const nextScheduledCall = extendedMetadata.next_scheduled_call || null;

  // Check if Voice AI is applicable (active or will be active)
  const isVoiceAIActive = isVoiceAIApplicable(
    request.channel?.type,
    request.status,
  );
  const willBeActive =
    request.status === 'pending' &&
    willVoiceAIBeApplicable(request.channel?.type);
  const showVoiceAI = isVoiceAIActive || willBeActive;

  const {
    nextScheduled,
    lastCall,
    isLoading: isVoiceAiLoading,
  } = useVoiceAISchedule(matterId, request.task_id, nextScheduledCall);

  const { nextLabel, lastLabel } = useMemo(() => {
    const format = (value: string | null) => {
      if (!value) return null;
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return null;
      return date.toLocaleString();
    };

    if (isVoiceAiLoading) {
      return { nextLabel: 'Loading…', lastLabel: 'Loading…' };
    }

    return {
      nextLabel: format(nextScheduled) || 'Not scheduled',
      lastLabel: format(lastCall) || 'No calls yet',
    };
  }, [isVoiceAiLoading, lastCall, nextScheduled]);

  // Find the current "up next" step
  const currentStepIndex = StatusOptions.findIndex(
    (status) => status.value === request.status,
  );
  const nextStepConfig = REQUEST_STEPS[currentStepIndex + 1];

  return (
    <div className="flex flex-col gap-3">
      {/* Voice AI info if applicable */}
      {showVoiceAI && (
        <div className="flex items-center gap-1.5 text-sm text-gray-600">
          <PhoneIcon className="h-3.5 w-3.5" />
          <span>Voice AI:</span>
          {isVoiceAIActive ? (
            <>
              <span className="uppercase text-[10px] text-gray-400">Next</span>
              <span className="font-medium text-gray-800">{nextLabel}</span>
              <span>•</span>
              <span className="uppercase text-[10px] text-gray-400">Last</span>
              <span className="font-medium text-gray-800">{lastLabel}</span>
              <span>•</span>
              <span>{getIntervalLabel(voiceInterval)}</span>
            </>
          ) : (
            <span>After submission</span>
          )}
        </div>
      )}

      {/* PRIMARY ACTION AREA */}
      <div className="max-w-min">
        {/* Action component - renders inline */}
        {nextStepConfig && (
          <nextStepConfig.component
            state="up_next"
            request={request}
            provider={provider}
            status={nextStepConfig.currentStatus}
            nextStatus={nextStepConfig.nextStatus}
            actionText={nextStepConfig.actionText}
            title={nextStepConfig.title}
          />
        )}
      </div>

      {/* FULL PROGRESS - collapsible */}
      <Disclosure>
        {({ open }) => (
          <>
            <Disclosure.Button className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-700 transition-colors">
              <ChevronDownIcon
                className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
              />
              <span>
                View all steps ({currentStepIndex + 1} of {REQUEST_STEPS.length}
                )
              </span>
            </Disclosure.Button>
            <Disclosure.Panel className="mt-3">
              <RecordsPizzaTracker request={request} provider={provider} />
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>
    </div>
  );
};
